<template>
  <div class="content-wrapper">
    <SendGiftMenu header="Product Details" />
    <div class="arrow-link">
      <router-link :to="{ name: 'GiftList' }">
        <span class="inter">Back</span>
      </router-link>
    </div>

    <!-- loader -->
    <section
      class="product-details-wrapper"
      v-if="!loadStatus && !bundleNotFound && !localBundleItems.length"
    >
      <div class="product-details-wrapper-grid loader">
        <div class="product-detail-image-wrapper">
          <div class="portrait-loader">
            <Skeleton height="80%" width="100%" />
          </div>
        </div>
        <div class="product-detail-content-wrapper">
          <div class="product-detail">
            <div class="product-detail-title loader">
              <Skeleton height="80%" width="100%" />
            </div>
            <div class="product-detail-price loader">
              <Skeleton height="40%" width="100%" />
            </div>
            <div class="product-detail-count loader">
              <Skeleton height="60%" width="30%" />
            </div>
          </div>
          <div class="product-description">
            <div class="gallery-description loader">
              <Skeleton height="30px" width="100%" />
            </div>
            <div class="gallery-description loader">
              <Skeleton height="20px" width="40%" />
            </div>
            <div class="gallery-description loader">
              <Skeleton height="20px" width="40%" />
            </div>
            <div class="gallery-description loader">
              <Skeleton height="20px" width="40%" />
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- loader -->

    <div class="bundle-detail-wrapper" v-if="bundleNotFound">
      <h1 id="bundle-not-found">Bundle not found</h1>
    </div>
    <!-- product details -->
    <section
      v-if="loadStatus && !bundleNotFound && localBundleItems.length"
      class="product-details-wrapper"
    >
      <div class="product-details-wrapper-grid">
        <div class="product-detail-image-wrapper">
          <div class="portrait-loader">
            <img :src="variantImage" @error="handleBrokenImage($event)" />
          </div>
        </div>
        <div class="product-detail-content-wrapper">
          <div class="product-detail">
            <div class="product-detail-title">
              {{
                bundleDetails.attributes ? bundleDetails.attributes.name : ''
              }}
            </div>
            <div class="product-detail-price inter">
              {{ retailPrice }}
            </div>
            <div class="product-detail-count inter">
              {{ inventoryDescription() }}
              <!-- <span v-if="nearestBundleItemsExpirationDates">
                 Consumables expiration
                {{ formatDate(nearestBundleItemsExpirationDates) }}
              </span> -->
            </div>
          </div>

          <div class="product-description">
            <p class="gallery-description inter">
              This gift set includes {{ localBundleItems.length }}
              {{ localBundleItems.length > 1 ? 'items' : 'item' }}. Please make
              any required item-specific selections.
            </p>

            <div class="product-bundle-lists" v-if="localBundleItems.length">
              <div
                v-for="(product, index) in localBundleItems"
                :key="product.bundleId * index"
              >
                <p class="inter">
                  {{ product.bundleQty }} x {{ product.productName }}
                </p>
              </div>
            </div>
            <div class="cart-actions">
              <div class="flex justify-start" v-if="!productExistingInCart">
                <button
                  class="button-gemnote full-width button-gemnote--small"
                  :class="{ 'button-gemnote--disabled': lowestOnInvetory <= 0 }"
                  :disabled="lowestOnInvetory <= 0"
                  @click="addToShoppingBag"
                >
                  <span>Add to cart</span>
                </button>
              </div>
              <div
                class="flex justify-start"
                v-if="productExistingInCart && productExistingInCart.quantity"
              >
                <button class="button-gemnote button-gemnote--small counter">
                  <button @click="decreaseItem">
                    <img src="../../assets/subtract-white.svg" alt="close" />
                  </button>

                  <span> {{ productExistingInCart.quantity }}</span>
                  <button @click="increaseItem">
                    <img src="../../assets/add-white.svg" alt="close" />
                  </button>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bundle-variants-list">
        <section class="w-full overflow-x-auto hide-scroll">
          <div class="bundle-variants" v-if="localBundleItems.length">
            <div
              class="bundle-variant"
              v-for="(product, index) in localBundleItems"
              :key="index"
            >
              <div class="bundle-variant-details">
                <div class="bundle-variant-details-image">
                  <img
                    :src="photoURL(product)"
                    @error="handleBrokenImage($event)"
                  />
                </div>
              </div>
              <p class="name">{{ product.productName }}</p>
              <div class="variant-details-container">
                <template v-if="product.typesToDisplay.length">
                  <div class="variant-details">
                    <p>Type</p>
                    <v-select
                      class="custom product-variant-select"
                      :options="
                        getTypeOptions(
                          product.typesToDisplay,
                          product.bundleId,
                          product.itemIndex
                        )
                      "
                      :placeholder="'Select type '"
                      :clearable="false"
                      :searchable="false"
                      :value="product.selectedType"
                      @input="setVariantType"
                    >
                      <template #open-indicator="{ attributes }">
                        <span v-bind="attributes"> <Dropdown /> </span>
                      </template>
                    </v-select>
                  </div>
                </template>

                <!-- section for product color -->
                <template v-if="product.colorsToDisplay.length">
                  <div class="variant-details">
                    <p>Color</p>
                    <div class="color-holder">
                      <p>Select color</p>
                      <div class="variant-colors">
                        <div
                          v-for="variantColor in product.colorsToDisplay"
                          :key="variantColor.id"
                          class="outside-box"
                          :class="[
                            {
                              active:
                                variantColor.color === product.selectedColor
                            },
                            {
                              'disabled-color': product.colorsToDisplay.length
                                ? variantColor.disabled
                                : ''
                            }
                          ]"
                          @click="
                            setVariantColor(
                              product.itemIndex,
                              variantColor.color
                            )
                          "
                        >
                          <div
                            class="color-box"
                            :style="{
                              backgroundColor: variantColor.hexCode
                                ? variantColor.hexCode
                                : 'lightgray'
                            }"
                          >
                            {{ variantColor.hexCode ? '' : '?' }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- section for product sizes -->
                <template v-if="product.sizesToDisplay.length">
                  <!-- <p class="type-header">Size</p> -->
                  <div class="variant-details">
                    <p>Size</p>
                    <v-select
                      class="custom product-variant-select"
                      :options="
                        getSizeOptions(
                          product.sizesToDisplay,
                          product.bundleId,
                          product.itemIndex
                        )
                      "
                      :selectable="isSelectable"
                      :placeholder="'Select Size'"
                      :clearable="false"
                      :searchable="false"
                      :value="product.selectedSize"
                      @input="setVariantSize"
                    >
                      <template #open-indicator="{ attributes }">
                        <span v-bind="attributes"> <Dropdown /> </span>
                      </template>
                    </v-select>
                  </div>
                </template>
                <!-- section for product expirationDate -->
                <template v-if="product.expirationDate">
                  <div class="variant-details">
                    <p class="expiration-date">
                      Good through
                      {{ formatMonthYear(product.expirationDate) }}
                    </p>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {
  progressStatus,
  layouts,
  defaultNotFoundImage,
  scrollPageToTop,
  handleBrokenImage,
  isCard
} from '../../helpers'
import Util from '../../mixins/Util'
import SendGiftMenu from '@/components/SendGiftMenu'
import Dropdown from '../Universal/Dropdown.vue'

export default {
  name: 'BundleDetail',
  mixins: [Util],
  props: ['id'],

  components: {
    SendGiftMenu,
    Dropdown,
    Skeleton: () => import('@/components/Universal/Skeleton')
  },

  data() {
    return {
      localBundleItems: [],
      retailPrice: 0,
      lowestOnInvetory: 0,
      handleBrokenImage: handleBrokenImage,
      variantImage: defaultNotFoundImage
    }
  },

  computed: {
    loadStatus() {
      return progressStatus(this.loadBundleDetails)
    },
    productName() {
      return (
        this.bundleDetails &&
        this.bundleDetails.attributes &&
        this.bundleDetails.attributes.name
      )
    },
    showPrices() {
      return this.$store.getters['team/showPrices']
    },
    productExistingInCart() {
      const bundles = this.shoppingBagProducts.filter(
        product => product.bundle_id === this.id
      )
      if (!bundles || !bundles.length) return false
      for(const bundle of bundles) {
        if (this.checkBundleItemsInCart(bundle)) {
          return bundle
        }
      }
    },
    ...mapState({
      loadBundleDetails: state => state.product.loadBundleDetails,
      bundleDetails: state => state.product.bundleDetails,
      bundleNotFound: state => state.product.bundleNotFound,
      bundleItems: state => state.product.bundleItems,
      shoppingBagProducts: state => state.order.shoppingBagProducts
    }),
    bundleItemsExpirationDates() {
      return this.bundleItems.map(bundleItem => bundleItem.expirationDate)
    },
    nearestBundleItemsExpirationDates() {
      const validDates = this.bundleItemsExpirationDates
        .filter(date => date) // Filter out null/undefined dates
        .map(date => new Date(date)) // Convert strings to Date objects

      if (!validDates.length) return null

      return validDates.reduce((nearest, current) =>
        current < nearest ? current : nearest
      )
    }
  },

  created() {
    this.$store.dispatch('layout/setPageLayout', layouts.PAIGE_DASHBOARD)
    this.$store.dispatch('product/getBundleDetails', this.$route.params.id)
    if (
      this.bundleDetails.attributes &&
      this.bundleDetails.attributes.images &&
      this.bundleDetails.attributes.images[0].url
    ) {
      this.variantImage = this.bundleDetails.attributes.images[0].url
    }
    scrollPageToTop()
  },

  methods: {
    checkBundleItemsInCart(bundle) {
      const cartBundleItems = bundle.items;
      const localBundleItems = this.localBundleItems.map(item =>  item.selectedVariant.id )
      const cartItemIds = new Set(cartBundleItems.map(obj => obj.id));
      const bundleItemsIds = new Set(localBundleItems);
      console.log({cartItemIds, bundleItemsIds})
      return  cartItemIds.size === bundleItemsIds.size && [...cartItemIds].every(value => bundleItemsIds.has(value));





      // // const cartItemIds = new Set(cartBundleItems.map(obj => obj.id));
      // // const bundleItemsIds = new Set(localBundleItems);
      // const bundleItems = bundle.items.map(item => item.id)
      // const localBundleItems = this.localBundleItems.map(item => item.id)
      //
      // return (
      //   bundleItems.length === localBundleItems.length &&
      //   bundleItems.every(value => localBundleItems.includes(value))
      // )
    },
    photoURL(product) {
      return product.selectedVariant.imageURL
        ? product.selectedVariant.imageURL
        : product.productVariants.length &&
          product.productVariants[0].representative_images &&
          product.productVariants[0].representative_images.length
        ? product.productVariants[0].representative_images[0].url
        : defaultNotFoundImage
    },
    setVariantType(variantType) {
      let { itemIndex } = variantType
      variantType = variantType.label
      let bundleIndex = this.localBundleItems.findIndex(
        bundle => bundle.itemIndex === itemIndex
      )

      this.localBundleItems[bundleIndex].selectedType = variantType
      this.localBundleItems[bundleIndex].selectedColor = null
      this.localBundleItems[bundleIndex].selectedSize = null
      this.localBundleItems[bundleIndex].selectedVariant = {
        id: null,
        name: '',
        displayName: '',
        type: null,
        color: null,
        size: null,
        price: null,
        imageURL: null,
        inventoriesCount: 0
      }

      // check available colors by type
      let { colorSizeDictionary } = this.localBundleItems[bundleIndex]
      let { colorsToDisplay } = this.localBundleItems[bundleIndex]
      let { sizesToDisplay } = this.localBundleItems[bundleIndex]

      // if there's colors to display we filter colors available by type
      if (colorsToDisplay.length) {
        let availableColorsByType = colorSizeDictionary.filter(
          colorSize => colorSize.type === variantType
        )

        for (let index = 0; index < colorsToDisplay.length; index++) {
          let foundColor = availableColorsByType.find(
            availableColor =>
              availableColor.color === colorsToDisplay[index].color
          )

          if (foundColor) {
            this.localBundleItems[bundleIndex].colorsToDisplay[
              index
            ].disabled = false
          } else {
            this.localBundleItems[bundleIndex].colorsToDisplay[
              index
            ].disabled = true
          }
        }

        // disable sizes
        this.localBundleItems[bundleIndex].sizesToDisplay.forEach(
          variantSize => {
            variantSize.disabled = true
          }
        )
      } else if (sizesToDisplay.length) {
        // Otherwise we filter sizes availables by type
        let availableSizesByType = colorSizeDictionary.filter(
          colorSize => colorSize.type === variantType
        )

        // if there's sizes to display
        for (let index = 0; index < sizesToDisplay.length; index++) {
          let foundSize = availableSizesByType.find(
            availableSize => availableSize.size === sizesToDisplay[index].size
          )

          if (foundSize) {
            this.localBundleItems[bundleIndex].sizesToDisplay[
              index
            ].disabled = false
          } else {
            this.localBundleItems[bundleIndex].sizesToDisplay[
              index
            ].disabled = true
          }
        }
      } else {
        this.setSelectedVariant(bundleIndex)
        this.updateBundlePrice()
      }
    },
    setVariantColor(itemIndex, variantColor) {
      let bundleIndex = this.localBundleItems.findIndex(
        bundle => bundle.itemIndex === itemIndex
      )

      this.localBundleItems[bundleIndex].selectedColor = variantColor
      this.localBundleItems[bundleIndex].selectedSize = null
      this.localBundleItems[bundleIndex].selectedVariant = {
        id: null,
        name: '',
        displayName: '',
        type: null,
        color: null,
        size: null,
        price: null,
        imageURL: null,
        inventoriesCount: 0
      }

      // check available sizes by color
      let { colorSizeDictionary } = this.localBundleItems[bundleIndex]

      let availableSizesByColor = colorSizeDictionary.filter(
        colorSize => colorSize.color === variantColor
      )

      let { sizesToDisplay } = this.localBundleItems[bundleIndex]

      if (sizesToDisplay.length) {
        for (let index = 0; index < sizesToDisplay.length; index++) {
          let foundSize = availableSizesByColor.find(
            availableSize =>
              availableSize.size === sizesToDisplay[index].size &&
              availableSize.type ===
                this.localBundleItems[bundleIndex].selectedType
          )

          if (foundSize) {
            this.localBundleItems[bundleIndex].sizesToDisplay[
              index
            ].disabled = false
          } else {
            this.localBundleItems[bundleIndex].sizesToDisplay[
              index
            ].disabled = true
          }
        }
      } else {
        this.setSelectedVariant(bundleIndex)
        this.updateBundlePrice()
      }
    },
    setVariantSize(variantSize) {
      let { itemIndex } = variantSize
      variantSize = variantSize.label
      let bundleIndex = this.localBundleItems.findIndex(
        bundle => bundle.itemIndex === itemIndex
      )

      this.localBundleItems[bundleIndex].selectedSize = variantSize
      this.setSelectedVariant(bundleIndex)
      this.updateBundlePrice()
    },
    setSelectedVariant(bundleIndex) {
      let foundVariant = []
      // all options are available (types - colors - sizes)
      if (
        this.localBundleItems[bundleIndex].typesToDisplay.length &&
        this.localBundleItems[bundleIndex].colorsToDisplay.length &&
        this.localBundleItems[bundleIndex].sizesToDisplay.length
      ) {
        this.localBundleItems[bundleIndex].productVariants.map(variant => {
          if (
            variant.spec.type ===
              this.localBundleItems[bundleIndex].selectedType &&
            variant.spec.color ===
              this.localBundleItems[bundleIndex].selectedColor &&
            variant.spec.size ===
              this.localBundleItems[bundleIndex].selectedSize
          ) {
            foundVariant.push({ ...variant })
          }
        })

        this.localBundleItems[bundleIndex].selectedVariant = {
          id: parseInt(foundVariant[0].id),
          name: foundVariant[0].product_name,
          displayName: foundVariant[0].product_name,
          type: foundVariant[0].spec.type ? foundVariant[0].spec.type : null,
          size: foundVariant[0].spec.size ? foundVariant[0].spec.size : '',
          color: foundVariant[0].spec.color ? foundVariant[0].spec.color : '',
          price: foundVariant[0].price,
          imageURL:
            foundVariant[0].representative_images.length > 0
              ? foundVariant[0].representative_images[0].url
              : null,
          inventoriesCount: foundVariant[0].inventories_count
        }
      } else if (
        this.localBundleItems[bundleIndex].colorsToDisplay.length &&
        this.localBundleItems[bundleIndex].sizesToDisplay.length &&
        !this.localBundleItems[bundleIndex].typesToDisplay.length
      ) {
        // only colors - sizes are available
        this.localBundleItems[bundleIndex].productVariants.map(variant => {
          if (
            variant.spec.color ===
              this.localBundleItems[bundleIndex].selectedColor &&
            variant.spec.size ===
              this.localBundleItems[bundleIndex].selectedSize
          ) {
            foundVariant.push({ ...variant })
          }
        })

        this.localBundleItems[bundleIndex].selectedVariant = {
          id: parseInt(foundVariant[0].id),
          name: foundVariant[0].product_name,
          displayName: foundVariant[0].product_name,
          type: foundVariant[0].spec.type ? foundVariant[0].spec.type : null,
          size: foundVariant[0].spec.size ? foundVariant[0].spec.size : '',
          color: foundVariant[0].spec.color ? foundVariant[0].spec.color : '',
          price: foundVariant[0].price,
          imageURL:
            foundVariant[0].representative_images.length > 0
              ? foundVariant[0].representative_images[0].url
              : null,
          inventoriesCount: foundVariant[0].inventories_count
        }
      } else if (
        !this.localBundleItems[bundleIndex].colorsToDisplay.length &&
        this.localBundleItems[bundleIndex].sizesToDisplay.length &&
        this.localBundleItems[bundleIndex].typesToDisplay.length
      ) {
        // only types - sizes are available

        this.localBundleItems[bundleIndex].productVariants.map(variant => {
          if (
            variant.spec.type ===
              this.localBundleItems[bundleIndex].selectedType &&
            variant.spec.size ===
              this.localBundleItems[bundleIndex].selectedSize
          ) {
            foundVariant.push({ ...variant })
          }
        })

        this.localBundleItems[bundleIndex].selectedVariant = {
          id: parseInt(foundVariant[0].id),
          name: foundVariant[0].product_name,
          displayName: foundVariant[0].product_name,
          type: foundVariant[0].spec.type ? foundVariant[0].spec.type : null,
          size: foundVariant[0].spec.size ? foundVariant[0].spec.size : '',
          color: foundVariant[0].spec.color ? foundVariant[0].spec.color : '',
          price: foundVariant[0].price,
          imageURL:
            foundVariant[0].representative_images.length > 0
              ? foundVariant[0].representative_images[0].url
              : null,
          inventoriesCount: foundVariant[0].inventories_count
        }
      } else if (
        this.localBundleItems[bundleIndex].colorsToDisplay.length &&
        !this.localBundleItems[bundleIndex].typesToDisplay.length &&
        !this.localBundleItems[bundleIndex].sizesToDisplay.length
      ) {
        // only colors are available
        this.localBundleItems[bundleIndex].productVariants.map(variant => {
          if (
            variant.spec.color ===
            this.localBundleItems[bundleIndex].selectedColor
          ) {
            foundVariant.push({ ...variant })
          }
        })

        this.localBundleItems[bundleIndex].selectedVariant = {
          id: parseInt(foundVariant[0].id),
          name: foundVariant[0].product_name,
          displayName: foundVariant[0].product_name,
          type: foundVariant[0].spec.type ? foundVariant[0].spec.type : null,
          size: foundVariant[0].spec.size ? foundVariant[0].spec.size : '',
          color: foundVariant[0].spec.color ? foundVariant[0].spec.color : '',
          price: foundVariant[0].price,
          imageURL:
            foundVariant[0].representative_images.length > 0
              ? foundVariant[0].representative_images[0].url
              : null,
          inventoriesCount: foundVariant[0].inventories_count
        }
      } else if (
        !this.localBundleItems[bundleIndex].colorsToDisplay.length &&
        !this.localBundleItems[bundleIndex].typesToDisplay.length &&
        this.localBundleItems[bundleIndex].sizesToDisplay.length
      ) {
        // only sizes are available
        this.localBundleItems[bundleIndex].productVariants.map(variant => {
          if (
            variant.spec.size ===
            this.localBundleItems[bundleIndex].selectedSize
          ) {
            foundVariant.push({ ...variant })
          }
        })

        this.localBundleItems[bundleIndex].selectedVariant = {
          id: parseInt(foundVariant[0].id),
          name: foundVariant[0].product_name,
          displayName: foundVariant[0].product_name,
          type: foundVariant[0].spec.type ? foundVariant[0].spec.type : null,
          size: foundVariant[0].spec.size ? foundVariant[0].spec.size : '',
          color: foundVariant[0].spec.color ? foundVariant[0].spec.color : '',
          price: foundVariant[0].price,
          imageURL:
            foundVariant[0].representative_images.length > 0
              ? foundVariant[0].representative_images[0].url
              : null,
          inventoriesCount: foundVariant[0].inventories_count
        }
      } else if (
        !this.localBundleItems[bundleIndex].colorsToDisplay.length &&
        this.localBundleItems[bundleIndex].typesToDisplay.length &&
        !this.localBundleItems[bundleIndex].sizesToDisplay.length
      ) {
        // only types are available
        this.localBundleItems[bundleIndex].productVariants.map(variant => {
          if (
            variant.spec.type ===
            this.localBundleItems[bundleIndex].selectedType
          ) {
            foundVariant.push({ ...variant })
          }
        })

        this.localBundleItems[bundleIndex].selectedVariant = {
          id: parseInt(foundVariant[0].id),
          name: foundVariant[0].product_name,
          displayName: foundVariant[0].product_name,
          type: foundVariant[0].spec.type ? foundVariant[0].spec.type : null,
          size: foundVariant[0].spec.size ? foundVariant[0].spec.size : '',
          color: foundVariant[0].spec.color ? foundVariant[0].spec.color : '',
          price: foundVariant[0].price,
          imageURL:
            foundVariant[0].representative_images.length > 0
              ? foundVariant[0].representative_images[0].url
              : null,
          inventoriesCount: foundVariant[0].inventories_count
        }
      }
    },
    inventoryDescription() {
      // check through all selected variants the lowest in stock to display
      let inventory_values = this.localBundleItems.map(bundleItem => {
        return bundleItem.selectedVariant.inventoriesCount
      })

      let minInStock = Math.min(...inventory_values)
      this.lowestOnInvetory = minInStock
      return `${minInStock} in stock`
    },
    updateBundlePrice() {
      let bundlePrice = 0
      this.localBundleItems.forEach(bundleItem => {
        let price =
          parseFloat(bundleItem.selectedVariant.price) *
          parseInt(bundleItem.bundleQty)

        bundlePrice += price
      })
      this.retailPrice = this.formatCurrency(
        Number.isNaN(bundlePrice) === true ? 0 : bundlePrice
      )
    },
    addToShoppingBag() {
      let errors = []
      let warnings = []

      this.localBundleItems.forEach(bundleItem => {
        if (bundleItem.expirationDate) {
          const expirationDate = new Date(bundleItem.expirationDate)

          const currentDate = new Date()
          const thirtyDaysFromNow = new Date()
          thirtyDaysFromNow.setDate(currentDate.getDate() + 30)

          if (expirationDate < currentDate) {
            warnings.push({
              type: 'warning',
              title: bundleItem.productName,
              messages: [
                `This product has a limited shelf life and expired on ${this.formatDate(
                  expirationDate
                )}. Please exercise discretion when placing an order.`
              ]
            })
          } else if (expirationDate <= thirtyDaysFromNow) {
            warnings.push({
              title: bundleItem.productName,
              type: 'warning',
              messages: [
                `This product has a limited shelf life and will expire on ${this.formatDate(
                  expirationDate
                )}. Please exercise discretion when placing an order.`
              ]
            })
          }
        }

        if (bundleItem.selectedVariant.price === null) {
          errors.push(
            `Can't add product ${bundleItem.productName} without price.`
          )
        }

        if (
          bundleItem.bundleQty > bundleItem.selectedVariant.inventoriesCount
        ) {
          errors.push(
            `Can't add product ${bundleItem.productName}, max quantity reached for this item`
          )
        }
      })

      if (errors.length) {
        this.$store.dispatch('notification/pushNotification', {
          type: 'error',
          messages: errors
        })
        return
      }

      if (warnings && warnings.length) {
        warnings.forEach((warning, index) => {
          setTimeout(() => {
            this.$store.dispatch('notification/pushNotification', warning)
          }, index * 5000)
        })
        return
      }
      this.handleBundleSelection()
    },
    handleBundleSelection() {
      const newBundle = {
        bundle_attributes: this.bundleDetails
          ? this.bundleDetails.attributes
          : null,
        type: 'bundle',
        bundle_id: this.id,
        quantity: 1,
        nearestBundleItemsExpirationDates: this
          .nearestBundleItemsExpirationDates,
        items: this.localBundleItems.map(bundleItem => {
          return (
            bundleItem && {
              ...bundleItem.selectedVariant,
              quantity: bundleItem.bundleQty,
              expirationDate: bundleItem.expirationDate
            }
          )
        })
      }

      this.$store.dispatch('order/addBundleItemToShoppingBag', newBundle)
    },
    getTypeOptions(variantTypes, bundleId, itemIndex) {
      let options = []
      for (let index = 0; index < variantTypes.length; index++) {
        const variantType = variantTypes[index]
        options.push({
          code: parseInt(variantType.id),
          label: variantType.type,
          bundleId,
          itemIndex
        })
      }

      return options.sort()
    },
    getSizeOptions(variantSizes, bundleId, itemIndex) {
      let options = []
      for (let index = 0; index < variantSizes.length; index++) {
        const variantSize = variantSizes[index]
        if (!variantSize.disabled) {
          options.push({
            code: parseInt(variantSize.id),
            label: variantSize.size,
            disabled: variantSize.disabled,
            bundleId,
            itemIndex
          })
        }
      }

      return options.sort()
    },
    isSelectable(value) {
      if (!value.disabled) {
        return true
      }
      return false
    },
    increaseItem() {
      if (this.productExistingInCart) {
        const index = this.shoppingBagProducts.findIndex(
          product => product.bundle_id === this.id
        )
        return this.$store.dispatch('order/increaseBundleItem', {
          ...this.productExistingInCart,
          index
        })
      }
    },
    decreaseItem() {
      if (this.productExistingInCart) {
        const index = this.shoppingBagProducts.findIndex(
          product => product.bundle_id === this.id
        )
        if (this.productExistingInCart.quantity === 1) {
          this.removeItem(this.productExistingInCart)
        } else {
          this.$store.dispatch('order/decreaseShoppingBagItem', {
            ...this.productExistingInCart,
            index
          })
        }
      }
    },
    removeItem(product) {
      if (product.attributes && isCard(product.attributes.tagList)) {
        localStorage.removeItem('selectedCard')
      }
      this.$store.dispatch('order/removeShoppingBagItem', product)
    },
    formatMonthYear(date) {
      const dateToFormat = new Date(date)
      const month = dateToFormat.getMonth() + 1
      const year = dateToFormat
        .getFullYear()
        .toString()
        .slice(-2)
      return `${month}/${year}`
    }
  },

  watch: {
    bundleItems() {
      this.localBundleItems = [...this.bundleItems]
      this.updateBundlePrice()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/global.scss';

.product-details {
  text-align: left;
  margin: 1.5rem 0;

  h2 {
    padding-bottom: 0.25rem;
    font-size: 2rem;
    font-weight: 600;
    color: $main-6;
    line-height: 44px;
  }

  p {
    font-weight: normal;
  }
}

.bundle-detail-wrapper {
  margin: 1rem 0;

  h2 {
    text-align: left;
  }

  h4 {
    text-align: left;
    font-size: 1.1em;
    margin: 1em 0;
  }
}

.product-name {
  font-size: 18px;
  font-weight: 700;
  text-align: left;
  margin: 1rem 0;
}

.product-price-stock {
  text-align: left;
  margin: 0.5rem 0;
  font-size: 16px;
  font-weight: normal;
}

.gallery-container {
  margin: 3.25rem auto;

  .button-gemnote--small {
    width: 11.125rem;
  }
}

.gallery-description {
  margin-bottom: 1.25rem;
  text-align: left;
  font-size: 0.938rem !important;
}

.bundle-gallery {
  display: flex;
  flex-wrap: wrap;
  grid-column-gap: 1.75rem;
  margin-bottom: 0rem;

  .bundle-holder {
    margin-bottom: 1.75rem;
  }
}

.bundle-gallery > * {
  flex: 0 1 17rem;
}

.product-details-wrapper {
  margin-top: 2.5rem;
  border: 1px solid #232220;
  border-radius: 10px;

  .product-details-wrapper-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: 480px;

    &.loader {
      min-height: 450px;
    }

    .product-detail-image-wrapper {
      background-color: #f9f9f9;
      margin: 8px;
      display: flex;
      align-items: center;
      justify-content: center;

      .portrait-loader {
        width: 50%;
        height: 90%;
        margin: 0px auto;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
    .product-detail-content-wrapper {
      border-left: 1px solid #232220;
      text-align: left;

      .product-detail {
        border-bottom: 1px solid #232220;
        padding: 16px 12px 12px 20px;

        .product-detail-title {
          text-transform: capitalize;
          font-family: $editorial;
          font-size: 2rem;
          line-height: 40px;
          width: 80%;

          &.loader {
            margin-top: 1rem;
            height: 50px;
          }
        }

        .product-detail-price {
          margin: 0.5rem 0;
          font-size: 1.363rem;
          font-weight: 600;
          color: #24292c;
          &.loader {
            margin-top: 1rem;
            height: 45px;
            width: 30%;
          }
        }

        .product-detail-count {
          padding-top: 0.5rem;
          font-size: 0.938rem;
          color: #232220;
          &.loader {
            padding-top: 0rem;
            height: 40px;
            width: 60%;
          }
        }
      }

      .product-description {
        padding-left: 20px;
        margin: 2rem 0;
        width: 90%;

        p {
          font-size: 0.938rem;
          color: #232220;
        }

        .product-bundle-lists {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }

        .cart-actions {
          margin-top: 2.25rem;

          .button-gemnote--small {
            font-weight: normal;
            width: 11.125rem;

            &.counter {
              width: 8.125rem;
              gap: 0.5rem;

              &:hover {
                background-color: #232220 !important;
                opacity: 1 !important;
              }
            }
          }
        }
      }
    }
  }

  .bundle-variants-list {
    border-top: 1px solid #232220;
    // padding: 1rem 0;

    .bundle-variants {
      display: flex;
      align-items: flex-start;
      // gap: 0.75rem;
      overflow-x: auto; /* Enable horizontal scrolling */
      white-space: nowrap; /* Prevent items from wrapping */
      scroll-behavior: smooth; /* Smooth scrolling */
    }

    /* Scrollbar styles */
    .bundle-variant {
      scrollbar-width: thin; /* Firefox thin scrollbar */
      scrollbar-color: #888 #f0f0f0; /* Thumb and track colors */
    }

    .bundle-variant::-webkit-scrollbar {
      height: 8px; /* Horizontal scrollbar height */
    }

    .bundle-variant::-webkit-scrollbar-track {
      background: #f0f0f0; /* Track background */
    }

    .bundle-variant::-webkit-scrollbar-thumb {
      background: #888; /* Thumb color */
      border-radius: 4px; /* Rounded corners for thumb */
    }

    .bundle-variant::-webkit-scrollbar-thumb:hover {
      background: #555; /* Darker thumb color on hover */
    }

    .bundle-variant {
      min-width: 222px;
      max-width: 222px;
      height: max-content;
      text-align: left;
      border-right: 0.5px solid #a09c95;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;

      .bundle-variant-details {
        padding: 8px;
        width: 100%;
        height: 100%;

        .bundle-variant-details-image {
          width: 100%;
          background-color: #f9f9f9;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          // max-width: 170px;
          height: 220px;

          img {
            width: 70%;
            max-width: 170px;
            max-height: 170px;
            object-fit: contain;
            margin: 0 auto;
            mix-blend-mode: multiply;
          }
        }
      }

      .name {
        border-top: 0.5px solid #a09c95;
        border-bottom: 0.5px solid #a09c95;
        text-transform: capitalize;
        width: 100%;
        padding: 0.5rem 0;
        padding-left: 12px;
        padding-right: 12px;
        font-size: 0.875rem;
        color: #232220;
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}

@media (max-width: 575.98px) {
  .product-details-wrapper {
    .product-details-wrapper-grid {
      display: grid;
      grid-template-columns: 1fr;

      .product-detail-image-wrapper {
        min-height: 350px;
      }

      .product-detail-content-wrapper {
        border-left: none;
        border-top: 1px solid #232220;
      }
    }
  }
}

.portrait {
  height: 290px;
  border: 1px solid $gray-variant-2;
  margin: 0 auto;

  img {
    padding: 2.5rem;
    width: 100%;
    height: 100%;
    max-width: 240px;
    object-fit: contain;
    display: block;
    margin: 0 auto;
  }
}

.portrait-info {
  bottom: 0;
  padding: 0rem 1rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  p {
    height: 2rem;
    font-size: 0.875rem;
    text-align: center;
    margin: 0.75rem 0px 1.75rem 0;
    font-weight: normal;
    color: $main-6;
  }

  .quantity {
    margin-bottom: 1.5rem;
    text-align: left;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    font-family: $inter;
    width: 40%;

    .multiply {
      margin-left: 7px;
    }

    .count {
      margin-left: auto;
    }
  }

  .type-header {
    margin: 0.5rem 0rem;
    padding: 0 1rem;
    text-align: left;
  }
}

.variant-details-container {
  padding: 12px;
  padding-bottom: 0px;
  min-height: 280px;
  max-height: 280px;
  overflow: auto;
  width: 100%;
  padding-top: 1.25rem !important;
  .variant-details {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 1.75rem;

    &.color {
      align-items: center;
    }

    .gift-select {
      width: 100%;
    }

    .product-variant-select {
      width: 90%;
    }

    p {
      width: 35%;
      font-family: $inter;
      font-size: 0.875rem;
      text-align: left;
      font-weight: 700;
      margin-bottom: 0rem;

      &.expiration-date {
        font-weight: 400;
      }

      span {
        font-family: $inter;
        font-weight: bold;
        font-weight: 600;
      }
    }
  }
}

.variant-type {
  // list-style: none;
  // display: flex;
  // flex-wrap: wrap;
  // justify-content: end;

  // li:first-child {
  //   margin-left: 0;
  // }

  // li {
  //   cursor: pointer;
  //   margin: 0.5rem 0.5rem 0.5rem 0rem;
  //   font-size: 16px;
  //   line-height: 20px;
  //   padding: 0.3rem 0.3rem;
  //   border-radius: 0px;
  //   background-color: transparent;
  // }

  display: flex;
  align-items: center;
  padding: 0;
  margin-bottom: 1.5rem;
  gap: 1rem;

  div {
    flex: 1;
  }
}

.selected-type {
  border: 0;
  border-bottom: 1px solid #25282a;
  color: $onyx;
  font-weight: 700;
}

.color-holder {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-top: 0.25rem;
  // margin-bottom: 0.5rem;

  p {
    width: max-content !important;
    font-family: $inter;
    font-size: 0.938rem;
    font-weight: 400 !important;
    width: max-content;
  }
}

.variant-colors {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0;
  // margin-top: 0.75rem;
  // margin-bottom: 1.5rem;

  .outside-box {
    width: 20px;
    height: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid transparent;
    box-sizing: content-box;
    // box-shadow: 0 0 2px grey !important;
  }

  .outside-box.active {
    border: 1px solid #000;
  }

  .color-box {
    color: $tab-ative;
    font-size: 0.875rem;
    cursor: pointer;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.disabled-color {
  pointer-events: none;
  opacity: 0.2;
}

.disabled-size {
  pointer-events: none;
  opacity: 0.2;
}

.variant-sizes {
  display: flex;
  align-items: center;
  padding: 0;
  margin-bottom: 1.5rem;
  gap: 1rem;

  div {
    flex: 1;
  }

  // li {
  //   cursor: pointer;
  //   margin: 0.5rem 0.5rem 0.5rem 0rem;
  //   font-size: 16px;
  //   line-height: 20px;
  //   padding: 0.3rem 0.3rem;
  //   border-radius: 0;
  //   background-color: transparent;
  // }
}

.selected-size {
  background-color: transparent;
  border: 0;
  border-bottom: 1px solid #25282a;
  color: $onyx;
  font-weight: 700;
}

.bundle-items-list {
  padding-bottom: 1.5rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
